import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import CommentSection from '@/pages/detail/components/CommentSection';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useFavorites } from '@/hooks/useFavorites';
import { getMovieById, getRelatedMovies, formatCount } from '@/lib/movieUtils';
import { BRAND_NAME } from '@/mocks/categories';

export default function DetailPage() {
  const { id = '' } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');
  const [playNotice, setPlayNotice] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPlayNotice(false);
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  usePageMeta({
    title: movie ? `${movie.title} - 影视详情 - ${BRAND_NAME}` : `影视详情 - ${BRAND_NAME}`,
    description: movie
      ? `${movie.title}（${movie.year} · ${movie.region}）${movie.summary.slice(0, 60)}... 在${BRAND_NAME}查看完整信息与相关推荐。`
      : `${BRAND_NAME}影视详情页`,
    keywords: movie
      ? `${movie.title},${movie.subCategoryName},国语对白,影音资源,${BRAND_NAME}`
      : `${BRAND_NAME},影视详情`,
  });

  if (!movie) {
    return (
      <PageShell>
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 text-xl font-bold text-foreground-900">未找到该资源</h1>
          <p className="mt-2 text-sm text-foreground-500">该影音资源可能已被移除，请从首页重新浏览。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </div>
      </PageShell>
    );
  }

  const related = getRelatedMovies(movie, 4);
  const favored = isFavorite(movie.id);
  const listId = `${movie.type}-${movie.subCategory}`;

  const handleFavorite = () => {
    const next = toggleFavorite(movie.id);
    setToast(next ? '已加入收藏' : '已取消收藏');
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast('链接已复制到剪贴板');
    } catch {
      setToast('复制失败，请手动复制地址栏链接');
    }
  };

  const metaItems = [
    { label: '类型', value: `${movie.typeName} · ${movie.subCategoryName}` },
    { label: '上映年份', value: String(movie.year) },
    { label: '地区', value: movie.region },
    { label: '时长', value: movie.duration },
    { label: '语言', value: movie.language },
    { label: '导演', value: movie.director },
  ];

  return (
    <PageShell>
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-10">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: movie.typeName, to: `/category/${movie.type}` },
            { label: movie.subCategoryName, to: `/list/${listId}` },
            { label: movie.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 md:gap-8">
          <div className="w-full max-w-[320px] mx-auto lg:mx-0">
            <div className="w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-100 border border-background-200">
              <img
                src={movie.cover}
                alt={`${movie.title} 高清封面`}
                title={`${movie.title} ${movie.subCategoryName}${movie.typeName}高清影音资源`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center justify-center gap-1.5 h-11 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  favored
                    ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                    : 'bg-background-50 border border-background-300 text-foreground-800 hover:border-accent-300 hover:text-accent-600'
                }`}
              >
                <i className={favored ? 'ri-bookmark-3-fill' : 'ri-bookmark-3-line'}></i>
                {favored ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center justify-center gap-1.5 h-11 rounded-md bg-background-50 border border-background-300 text-foreground-800 text-sm font-medium whitespace-nowrap hover:border-secondary-300 hover:text-secondary-600 transition-colors cursor-pointer"
              >
                <i className="ri-share-line"></i>
                分享
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center flex-wrap gap-2">
              <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                {movie.typeName}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium">
                {movie.subCategoryName}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-semibold">
                <i className="ri-star-fill"></i>
                {movie.rating.toFixed(1)}
              </span>
            </div>

            <h1 className="mt-3 text-2xl md:text-3xl font-bold text-foreground-950">{movie.title}</h1>

            <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-play-circle-line text-accent-500"></i>
                播放 {formatCount(movie.playCount)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-bookmark-line text-secondary-500"></i>
                收藏 {formatCount(movie.favoriteCount)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                更新于 {movie.updatedAt}
              </span>
            </div>

            <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {metaItems.map((item) => (
                <div key={item.label} className="flex gap-3 text-sm">
                  <dt className="text-foreground-500 shrink-0 w-16">{item.label}</dt>
                  <dd className="text-foreground-800">{item.value}</dd>
                </div>
              ))}
              <div className="flex gap-3 text-sm sm:col-span-2">
                <dt className="text-foreground-500 shrink-0 w-16">主演</dt>
                <dd className="text-foreground-800">{movie.cast.join(' / ')}</dd>
              </div>
            </dl>

            <div className="mt-4 flex flex-wrap gap-2">
              {movie.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-5">
              <h2 className="text-lg font-bold text-foreground-950 mb-2">剧情简介</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">{movie.summary}</p>
            </div>
          </div>
        </div>

        <section className="mt-10">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-1 h-7 rounded-full bg-accent-500"></span>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">在线播放（界面演示）</h2>
          </div>
          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-primary-950">
            <img
              src={movie.banner || movie.cover}
              alt={`${movie.title} 播放预览`}
              title={`${movie.title} 播放界面演示`}
              className="w-full h-full object-cover object-top opacity-50"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setPlayNotice(true)}
                aria-label="播放演示"
                className="w-16 h-16 rounded-full bg-accent-500 text-background-50 flex items-center justify-center hover:bg-accent-600 transition-colors cursor-pointer"
              >
                <i className="ri-play-fill text-3xl"></i>
              </button>
              <p className="text-sm text-white/80">播放功能仅为界面演示，不具备真实播放能力</p>
            </div>
          </div>
          {playNotice && (
            <div className="mt-3 flex items-center gap-2 px-4 py-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-700">
              <i className="ri-information-line"></i>
              当前为演示站点，无法真实播放视频。请通过正规渠道观看正版影视作品。
            </div>
          )}
        </section>

        <section className="mt-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">所属分类：</span>
            <Link
              to={`/category/${movie.type}`}
              className="px-3 py-1.5 rounded-full bg-primary-50 text-primary-500 text-sm hover:bg-primary-500 hover:text-background-50 transition-colors"
            >
              {movie.typeName}
            </Link>
            <Link
              to={`/list/${listId}`}
              className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-sm hover:bg-secondary-500 hover:text-background-50 transition-colors"
            >
              {movie.subCategoryName}
            </Link>
          </div>
        </section>

        <CommentSection />

        {related.length > 0 && (
          <section className="mt-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-1 h-7 rounded-full bg-accent-500"></span>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950">相关推荐</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} showSummary={false} />
              ))}
            </div>
          </section>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-primary-900 text-background-50 text-sm animate-fade-in">
          {toast}
        </div>
      )}
    </PageShell>
  );
}